import React, { createContext, useContext, useEffect, useState } from "react";

export interface OrderItem {
  _id?: string;
  itemId: string;
  name: string;
  kitchenName?: string;
  cartId: string;
  price: number;
  dealId?: string;
  /** Name of the deal this line belongs to (receipts / kitchen tickets) */
  dealName?: string;
  originalPrice?: number;
  quantity: number;
    categoryId: string
    isPartOfDeal?: boolean;
    tax: number;
    gst?: number;
    pst?: number;
  discountAmount?: string;
  imageUrl?: string;
  customOptions?: { label: string; price: number; qty: number }[];
}
type DealType = 'percentage_discount' | 'fixed_price' | 'buy_x_get_y' | 'build_your_own' | 'bundle';

interface AppliedDeal {
  dealId: string;        // unique per claim (claimId for BYO/bundle, deal._id for standard)
  originalDealId?: string; // actual MongoDB deal _id — used for maxUsagePerOrder counting
  name: string;
  discountAmount: number;
    dealType: DealType;
  byoBasePrice?: number;
  byoIncludedToppings?: number;
  bundlePrice?: number;
  bundleFixedItemsTotal?: number;
}
interface OrderData {
  items?: OrderItem[];
  totalAmount?: string;
  discountAmount?: string;
  appliedDeals?: AppliedDeal[];
  phoneNumber?: string;
  tableToken?: number | string;
  choice?: string; // Dining In / Takeaway
  payChoice?: string; // Pay Here / Counter
  customOptions?: { label: string; price: number; qty: number }[];
}

interface OrderContextType {
  orderData: OrderData | null;
  setOrderData: React.Dispatch<React.SetStateAction<OrderData | null>>;
  discount: number | null;
  setDiscount: React.Dispatch<React.SetStateAction<number | null>>;
  logoUrl: string;
  setLogoUrl: React.Dispatch<React.SetStateAction<string>>;
  clearOrder: () => void;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const OrderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [discount, setDiscount] = useState<number | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.sessionStorage.getItem("logo_url") || "";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (logoUrl) {
      window.sessionStorage.setItem("logo_url", logoUrl);
    } else {
      window.sessionStorage.removeItem("logo_url");
    }
  }, [logoUrl]);
  // ✅ Add clearOrder implementation
  const clearOrder = () => setOrderData(null);

  return (
    <OrderContext.Provider
      value={{
        orderData,
        setOrderData,
        clearOrder,
        discount,
        setDiscount,
        logoUrl,
        setLogoUrl,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrder must be used inside OrderProvider");
  }
  return context;
};
